import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DataService } from 'src/app/data.service';

/**
 *  Dolgozói dokumentumgenerátor (a régi n8n „DOKSIGENERÁTOR" workflow kiváltása).
 *
 *  A sablon a kérvényszerkesztőben készül (dokumentum típus), az adat az állományból
 *  és a pótlék-táblából jön, a kimenet pedig nyomtatható összefűzött PDF és/vagy
 *  dolgozónkénti (opcionálisan aláírt) PDF a dokumentumtárba.
 */
@Component({
  selector: 'app-doksigenerator',
  templateUrl: './doksigenerator.component.html',
  styleUrls: ['./doksigenerator.component.scss'],
})
export class DoksigeneratorComponent implements OnInit {
  sajatIntezmeny: any;
  jogosult = false;
  betoltes = false;

  ev: number | null = null;
  sablonok: any[] = [];
  sablon: string | null = null;

  // Generálási menetek: egy sablonból többször is kell generálni (más-más adatokkal),
  // ezért a félkész munka menetenként külön él.
  menetek: any[] = [];
  menet: string | null = null;

  dolgozok: any[] = [];
  kereses = '';

  // Közös pótlék-gyűjtő: egyszer felvitt jogcímek, amiket dolgozónként be lehet pipálni.
  jogcimek: any[] = [];
  gyujtoNyitva = false;

  // Mentetlen változás jelzése (nem muszáj azonnal generálni)
  piszkos = false;
  mentve: string | null = null;

  // A sablon szabad mezői (a szövegben {{hatalyos}}, {{megjegyzes}} néven hivatkozhatók)
  mezok: any = { hatalyos: '', megjegyzes: '' };
  kelt = '';
  /** Melyik nap érvényes bérét töltse be a bér modulból (a hatálybalépés napja). */
  berdatum = '';

  kimenet: any = {
    osszefuzott: true, kulon: false, alairt: false, docx: false,
    // Oldalszám a lap alján; dolgozónként (a sablon elejétől) újraindul.
    oldalszam: true, oldalszam_forma: 'per',
  };

  mezoKatalogus: any = null;
  elonezetHtml: SafeHtml | null = null;
  elonezetNev = '';
  folyamatban = false;

  // Új sablon
  ujSablonForm = false;
  ujSablonNev = '';

  constructor(
    private http: HttpClient,
    private data: DataService,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    const jogkor = await this.data.getJogkor();
    // A hozzáférés a Jogosultságok szerkesztőből jön, nem az 'admin' jogkör nevéből –
    // így más jogkör is megkaphatja ezt a képernyőt (/admin/doksigenerator).
    this.jogosult = (await this.data.hozzaferesek()).szabad('/admin/doksigenerator');
    this.ev = await this.data.getMunkatanev();
    const ma = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    this.berdatum = `${ma.getFullYear()}-${p(ma.getMonth() + 1)}-${p(ma.getDate())}`;
    this.sablonokBetolt();
    this.jogcimekBetolt();
    this.dolgozokBetolt();
    this.http.get<any>('/api/doksigenerator/mezok').subscribe({
      next: (r) => { this.mezoKatalogus = r; },
      error: () => { /* a súgó nélkül is működik */ },
    });
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  sablonokBetolt() {
    this.http.get<any[]>(`/api/doksigenerator/sablonok/${this.intezmenyId}`).subscribe({
      next: (r) => {
        this.sablonok = Array.isArray(r) ? r : [];
        if (!this.sablon && this.sablonok.length) this.sablon = this.sablonok[0].id;
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a sablonokat.', 'error'),
    });
  }

  dolgozokBetolt() {
    if (!this.intezmenyId) return;
    this.betoltes = true;
    this.http.get<any>(`/api/doksigenerator/dolgozok/${this.intezmenyId}`
      + `?ev=${this.ev || ''}&datum=${this.berdatum || ''}`).subscribe({
      next: (r) => {
        this.betoltes = false;
        this.ev = r?.ev ?? this.ev;
        this.dolgozok = (r?.dolgozok || []).map((d: any) => ({
          ...d,
          kivalasztva: false,
          nyitva: false,
          iktatoszam: '',
          megjegyzes: '',
          potlekok: (d.potlekok || []).map((p: any) => ({ ...p })),
          // A törzsadatból jövő értékek megőrzése: új menet ezekből indul.
          alapber_torzs: d.alapber,
          potlekok_torzs: (d.potlekok || []).map((p: any) => ({ ...p })),
        }));
        this.sorokEpit();
        this.menetekBetolt();
      },
      error: (e) => {
        this.betoltes = false;
        this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni az állományt.', 'error');
      },
    });
  }

  /* ── Pótlék-gyűjtő (a bér menüből) ── */

  /**
   *  A jogcímeket a BÉR MENÜ tartja karban (Admin → Dolgozók → Bérek és pótlékok),
   *  itt csak felhasználjuk őket – így a kettő nem tud szétcsúszni.
   */
  jogcimekBetolt() {
    if (!this.intezmenyId) return;
    this.http.get<any[]>(`/api/allomany-ber/jogcimek/${this.intezmenyId}`).subscribe({
      next: (r) => {
        this.jogcimek = (Array.isArray(r) ? r : []).map((j) => ({ ...j }));
        this.sorokEpit();
      },
      error: () => { /* gyűjtő nélkül is felvihető egyedi pótlék */ },
    });
  }

  /** Átugrás a bér menübe, ahol a jogcímek és a bértörténet karbantartható. */
  berMenu() {
    this.router.navigateByUrl('/admin/ber');
  }

  /**
   *  A dolgozói bérsorok újraépítése a gyűjtő aktuális sorrendjében.
   *  Minden gyűjtőbeli jogcím kap egy pipálható sort (a dolgozónál rögzített összeggel,
   *  vagy ha még nincs, a jogcím alapösszegével); ami nem szerepel a gyűjtőben,
   *  az egyedi sorként marad meg – így korábbi pótlék nem veszhet el.
   */
  sorokEpit() {
    for (const d of this.dolgozok) {
      const aktualis = d.sorok ? this.potlekokOssze(d) : (d.potlekok || []);
      const meglevo = new Map<string, any>();
      for (const p of aktualis) meglevo.set(String(p.jogcim || '').trim(), p);

      d.sorok = this.jogcimek.map((j) => {
        const nev = String(j.nev || '').trim();
        const p = meglevo.get(nev);
        meglevo.delete(nev);
        return {
          jogcim: nev,
          osszeg: p ? Number(p.osszeg) || 0 : Number(j.alap_osszeg) || 0,
          be: !!p,
        };
      });
      d.egyeb = [...meglevo.values()]
        .filter((p) => String(p.jogcim || '').trim())
        .map((p) => ({ jogcim: p.jogcim, osszeg: Number(p.osszeg) || 0 }));
    }
  }

  /** A dolgozó tényleges pótléksorai: a bepipált gyűjtő-sorok (gyűjtő-sorrendben) + az egyediek. */
  potlekokOssze(d: any): any[] {
    const gyujtobol = (d.sorok || [])
      .filter((s: any) => s.be)
      .map((s: any) => ({ jogcim: s.jogcim, osszeg: Number(s.osszeg) || 0 }));
    const egyediek = (d.egyeb || [])
      .filter((s: any) => String(s.jogcim || '').trim())
      .map((s: any) => ({ jogcim: String(s.jogcim).trim(), osszeg: Number(s.osszeg) || 0 }));
    return [...gyujtobol, ...egyediek];
  }

  /** Pipálás: bekapcsoláskor az alapösszeg jön, ha a dolgozónál még nem volt érték. */
  jogcimValt(d: any, sor: any, be: boolean) {
    sor.be = be;
    if (be && !Number(sor.osszeg)) {
      const j = this.jogcimek.find((x) => String(x.nev || '').trim() === sor.jogcim);
      sor.osszeg = Number(j?.alap_osszeg) || 0;
    }
    this.valtozott();
  }

  /* ── Generálási menetek ── */

  /**
   *  Egy sablonból többször is kell generálni (pl. januári és szeptemberi
   *  kinevezésmódosítás), más-más adatokkal. Minden menet önálló: saját közös mezők,
   *  kijelölés, kimenet, és dolgozónként saját bérsor, iktatószám, megjegyzés.
   */
  menetekBetolt(valasztando?: string) {
    if (!this.intezmenyId || !this.sablon) { this.menetek = []; this.menet = null; return; }
    this.http.get<any>(`/api/doksigenerator/menetek/${this.intezmenyId}`
      + `?ev=${this.ev || ''}&sablon=${encodeURIComponent(this.sablon)}`).subscribe({
      next: (r) => {
        this.menetek = r?.menetek || [];
        const kivant = valasztando || this.menet;
        const talalt = this.menetek.find((m: any) => m.id === kivant);
        this.menet = talalt ? talalt.id : (this.menetek[0]?.id || null);
        this.menetVisszatolt();
      },
      error: () => { /* menet nélkül is generálható, csak nem őrződik meg */ },
    });
  }

  get aktualisMenet(): any {
    return this.menetek.find((m) => m.id === this.menet) || null;
  }

  /** Menetváltás: a felület teljesen a választott menet adataira áll. */
  menetValt() {
    this.elonezetHtml = null;
    this.menetVisszatolt();
  }

  /**
   *  A választott menet adatainak betöltése a felületre. Ha nincs menet, a törzsadat
   *  (állományi alapbér + a tanév pótléksorai) marad – abból indul az új menet.
   */
  private menetVisszatolt() {
    const m = this.aktualisMenet;
    const adat = m?.adat || {};
    this.mentve = m?.modositva || null;

    this.mezok = { hatalyos: '', megjegyzes: '', ...(adat.mezok || {}) };
    this.kelt = adat.kelt || '';
    if (adat.berdatum) this.berdatum = adat.berdatum;
    this.kimenet = {
      osszefuzott: true, kulon: false, alairt: false, docx: false,
      oldalszam: true, oldalszam_forma: 'per',
      ...(adat.kimenet || {}),
    };

    const kijelolt = new Set(adat.kivalasztott || []);
    const dolgozoAdat = adat.dolgozok || {};
    for (const d of this.dolgozok) {
      const s = dolgozoAdat[d.adid];
      d.kivalasztva = kijelolt.has(d.adid);
      d.iktatoszam = s?.iktatoszam || '';
      d.megjegyzes = s?.megjegyzes || '';
      d.alapber = s && s.alapber !== undefined ? s.alapber : d.alapber_torzs;
      d.potlekok = (s?.potlekok || d.potlekok_torzs || []).map((p: any) => ({ ...p }));
      d.sorok = null;   // a sorokEpit a fenti potlekok-ból építi újra a pipálható listát
    }
    this.sorokEpit();
    this.piszkos = false;
  }

  /** A menetbe mentendő teljes állapot. */
  private menetAdat(): any {
    const dolgozok: any = {};
    for (const d of this.dolgozok) {
      dolgozok[d.adid] = {
        alapber: Number(d.alapber) || 0,
        potlekok: this.potlekokOssze(d),
        iktatoszam: d.iktatoszam || '',
        megjegyzes: d.megjegyzes || '',
      };
    }
    return {
      mezok: this.mezok,
      kelt: this.kelt,
      berdatum: this.berdatum,
      kimenet: this.kimenet,
      kivalasztott: this.kivalasztottak.map((d) => d.adid),
      dolgozok,
    };
  }

  /**
   *  A bérsorok újratöltése a bér modulból a megadott napra. A menetben eltárolt
   *  (esetleg kézzel felülírt) összegeket szándékosan felülírja – ezt a gombot akkor
   *  nyomjuk, ha időközben rögzítettek egy bérváltozást.
   */
  berFrissit() {
    if (!this.intezmenyId) return;
    this.http.get<any>(`/api/doksigenerator/dolgozok/${this.intezmenyId}`
      + `?ev=${this.ev || ''}&datum=${this.berdatum || ''}`).subscribe({
      next: (r) => {
        const terkep = new Map<string, any>();
        for (const x of (r?.dolgozok || [])) terkep.set(x.adid, x);
        for (const d of this.dolgozok) {
          const uj = terkep.get(d.adid);
          if (!uj) continue;
          d.alapber = uj.alapber;
          d.potlekok = (uj.potlekok || []).map((p: any) => ({ ...p }));
          d.alapber_torzs = uj.alapber;
          d.potlekok_torzs = (uj.potlekok || []).map((p: any) => ({ ...p }));
          d.sorok = null;
        }
        this.sorokEpit();
        this.valtozott();
        this.data.sendToast(`Bérsorok frissítve a(z) ${this.berdatum} napra érvényes bérrel.`);
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A bérsorok frissítése nem sikerült.', 'error'),
    });
  }

  /** Új menet: üresen (a törzsadatból) vagy a mostani menet másolataként. */
  ujMenet(masolat = false) {
    if (!this.sablon) { this.data.sendToast('Előbb válassz sablont.', 'warning'); return; }
    const alap = masolat ? `${this.aktualisMenet?.nev || 'Menet'} másolata` : '';
    const nev = (window.prompt('A menet neve (pl. „2026. szeptemberi"):', alap) || '').trim();
    if (!nev) return;

    this.http.post<any>(`/api/doksigenerator/menet/${this.intezmenyId}`, {
      sablon: this.sablon,
      ev: this.ev,
      nev,
      masolat: masolat ? this.menet : null,
      adat: masolat ? null : this.menetAdat(),
    }).subscribe({
      next: (r) => {
        this.menetekBetolt(r.id);
        this.data.sendToast(`„${nev}" menet létrehozva.`);
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A menet létrehozása nem sikerült.', 'error'),
    });
  }

  atnevezMenet() {
    const m = this.aktualisMenet;
    if (!m) return;
    const nev = (window.prompt('A menet új neve:', m.nev) || '').trim();
    if (!nev || nev === m.nev) return;
    this.http.put<any>(`/api/doksigenerator/menet/${m.id}`, { nev }).subscribe({
      next: () => { m.nev = nev; this.data.sendToast('Átnevezve.'); },
      error: (e) => this.data.sendToast(e?.error?.error || 'Az átnevezés nem sikerült.', 'error'),
    });
  }

  torolMenet() {
    const m = this.aktualisMenet;
    if (!m) return;
    if (!confirm(`Törlöd a(z) „${m.nev}" menetet? A már legenerált dokumentumok megmaradnak.`)) return;
    this.http.delete<any>(`/api/doksigenerator/menet/${m.id}`).subscribe({
      next: () => { this.menet = null; this.menetekBetolt(); this.data.sendToast('Menet törölve.'); },
      error: (e) => this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error'),
    });
  }

  /** Bármilyen szerkesztés után jelezzük, hogy van mentetlen változás. */
  valtozott() {
    this.piszkos = true;
  }

  /**
   *  A teljes előkészített állapot mentése generálás nélkül – hogy ne kelljen
   *  azonnal generálni. Ha még nincs menet, előbb létrehozunk egyet.
   */
  mentes(utana?: () => void) {
    if (!this.sablon) { this.data.sendToast('Előbb válassz sablont.', 'warning'); return; }
    if (!this.menet) { this.ujMenet(false); return; }

    this.folyamatban = true;
    this.http.put<any>(`/api/doksigenerator/menet/${this.menet}`, { adat: this.menetAdat() }).subscribe({
      next: () => {
        this.folyamatban = false;
        this.piszkos = false;
        this.mentve = new Date().toISOString();
        const m = this.aktualisMenet;
        if (m) m.adat = this.menetAdat();
        if (utana) { utana(); return; }
        this.data.sendToast('A menet elmentve – később folytatható.', 'success');
      },
      error: (e) => {
        this.folyamatban = false;
        this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
      },
    });
  }

  /** Sablonváltás: a menetek sablononként külön élnek. */
  sablonValt() {
    this.elonezetHtml = null;
    this.menet = null;
    this.menetekBetolt();
  }

  /** Van-e a dolgozónak saját megjegyzése (a listában jelezzük, csukott soron is). */
  vanMegjegyzes(d: any): boolean {
    return !!String(d?.megjegyzes || '').trim();
  }

  get szurtDolgozok(): any[] {
    const q = this.kulcs(this.kereses);
    if (!q) return this.dolgozok;
    return this.dolgozok.filter((d) =>
      this.kulcs(`${d.viselt_vez} ${d.viselt_ker} ${d.adid}`).includes(q));
  }

  private kulcs(s: any): string {
    return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  get kivalasztottak(): any[] {
    return this.dolgozok.filter((d) => d.kivalasztva);
  }

  mindKivalaszt(ertek: boolean) {
    for (const d of this.szurtDolgozok) d.kivalasztva = ertek;
  }

  /** Egy dolgozó bérsorainak összege (alapbér + pótlékok) – a listában látszik. */
  osszesen(d: any): number {
    return Number(d.alapber || 0)
      + this.potlekokOssze(d).reduce((s: number, p: any) => s + Number(p.osszeg || 0), 0);
  }

  ft(v: any): string {
    const n = Math.round(Number(v) || 0);
    return `${n.toLocaleString('hu-HU')} Ft`;
  }

  /** Egyedi (gyűjtőn kívüli) pótléksor – ami csak ennél a dolgozónál fordul elő. */
  ujPotlek(d: any) {
    if (!d.egyeb) d.egyeb = [];
    d.egyeb.push({ jogcim: '', osszeg: 0 });
    this.valtozott();
  }

  torolPotlek(d: any, i: number) {
    d.egyeb.splice(i, 1);
    this.valtozott();
  }

  /**
   *  A dolgozó sorából indított mentés. A bérsor is a MENETBE kerül (nem a törzsbe),
   *  hogy egy másik menet – például a szeptemberi kinevezésmódosítás – adatait
   *  ne írja át, és a korábban kiadott irat számai se változzanak visszamenőleg.
   */
  mentBer(d: any) {
    this.mentes();
  }

  /* ── Sablonkezelés ── */

  ujSablon() {
    const nev = this.ujSablonNev.trim();
    if (!nev) { this.data.sendToast('Adj nevet a sablonnak.', 'warning'); return; }
    this.http.post<any>(`/api/doksigenerator/sablon/${this.intezmenyId}`, { name: nev }).subscribe({
      next: (r) => {
        this.ujSablonForm = false;
        this.ujSablonNev = '';
        this.sablon = r.id;
        this.sablonokBetolt();
        this.data.sendToast('Sablon létrehozva. Nyisd meg a szerkesztőben a szöveg megírásához.');
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'Nem sikerült létrehozni.', 'error'),
    });
  }

  torolSablon() {
    const s = this.sablonok.find((x) => x.id === this.sablon);
    if (!s) return;
    if (!confirm(`Törlöd a(z) „${s.name}" sablont?`)) return;
    this.http.delete<any>(`/api/doksigenerator/sablon/${s.id}`).subscribe({
      next: () => { this.sablon = null; this.sablonokBetolt(); this.data.sendToast('Sablon törölve.'); },
      error: (e) => this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error'),
    });
  }

  /** A sablon szövegének szerkesztése a meglévő kérvényszerkesztőben. */
  szerkesztes() {
    if (!this.sablon) { this.data.sendToast('Előbb válassz sablont.', 'warning'); return; }
    this.router.navigate(['/kerveny'], { queryParams: { sablon: this.sablon, mod: 'szerkeszto' } });
  }

  /* ── Előnézet és generálás ── */

  private torzsBody() {
    return {
      sablon: this.sablon,
      menet: this.menet,
      ev: this.ev,
      mezok: { ...this.mezok, ...(this.kelt ? { kelt: this.kelt } : {}) },
      // A kimenet az előnézet-PDF-hez is kell (oldalszámozás, oldalméret).
      kimenet: this.kimenet,
      iktatoszamok: this.dolgozonkent('iktatoszam'),
      megjegyzesek: this.dolgozonkent('megjegyzes'),
    };
  }

  /** A dolgozónkénti szabad mezők { adid: érték } alakban (üreseket kihagyva). */
  private dolgozonkent(mezo: string): any {
    const ertekek: any = {};
    for (const d of this.dolgozok) {
      const ertek = String(d?.[mezo] || '').trim();
      if (ertek) ertekek[d.adid] = ertek;
    }
    return ertekek;
  }

  /**
   *  Az előnézet és a generálás a MENTETT bérsorokból dolgozik (azok az adatbázisban
   *  élnek), ezért a nyitott szerkesztés eredményét előbb elmentjük – különben a
   *  frissen beírt összeg nem látszana a dokumentumon.
   */
  private mentesUtan(muvelet: () => void) {
    if (this.piszkos && this.sablon) this.mentes(muvelet);
    else muvelet();
  }

  elonezet() {
    if (!this.sablon) { this.data.sendToast('Előbb válassz sablont.', 'warning'); return; }
    const elso = this.kivalasztottak[0] || this.dolgozok[0];
    if (!elso) { this.data.sendToast('Nincs dolgozó az előnézethez.', 'warning'); return; }
    this.mentesUtan(() => this.elonezetFut(elso));
  }

  private elonezetFut(elso: any) {
    this.folyamatban = true;
    this.http.post<any>(`/api/doksigenerator/elonezet/${this.intezmenyId}`,
      { ...this.torzsBody(), adid: elso.adid }).subscribe({
      next: (r) => {
        this.folyamatban = false;
        this.elonezetNev = r.dolgozo;
        this.elonezetHtml = this.sanitizer.bypassSecurityTrustHtml(r.html);
      },
      error: (e) => {
        this.folyamatban = false;
        this.data.sendToast(e?.error?.error || 'Az előnézet nem készült el.', 'error');
      },
    });
  }

  /** Az előnézet lementése PDF-ként – ugyanaz a konverzió fut, mint generáláskor. */
  elonezetPdf() {
    if (!this.sablon) { this.data.sendToast('Előbb válassz sablont.', 'warning'); return; }
    const elso = this.kivalasztottak[0] || this.dolgozok[0];
    if (!elso) { this.data.sendToast('Nincs dolgozó az előnézethez.', 'warning'); return; }
    this.mentesUtan(() => this.elonezetPdfFut(elso));
  }

  private elonezetPdfFut(elso: any) {
    this.folyamatban = true;
    this.http.post(`/api/doksigenerator/elonezet/${this.intezmenyId}`,
      { ...this.torzsBody(), adid: elso.adid, pdf: true },
      { responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        this.folyamatban = false;
        const nev = (this.sablonok.find((s) => s.id === this.sablon)?.name || 'dokumentum')
          .replace(/[^\wÀ-ſ-]+/g, '_');
        const kihez = `${elso.viselt_vez || ''}_${elso.viselt_ker || ''}`.replace(/[^\wÀ-ſ-]+/g, '_');
        this.blobLetolt(blob, `${nev}-${kihez}-elonezet.pdf`);
      },
      error: async (e) => {
        this.folyamatban = false;
        let uzenet = 'Az előnézet PDF nem készült el.';
        if (e?.error instanceof Blob) {
          try { uzenet = JSON.parse(await e.error.text())?.error || uzenet; } catch { /* marad az alap */ }
        }
        this.data.sendToast(uzenet, 'error');
      },
    });
  }

  /** Letöltés indítása memóriában lévő fájlra. */
  private blobLetolt(blob: Blob, fajlnev: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fajlnev;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  generalas() {
    if (!this.sablon) { this.data.sendToast('Előbb válassz sablont.', 'warning'); return; }
    const kivalasztva = this.kivalasztottak;
    if (!kivalasztva.length) { this.data.sendToast('Jelölj ki legalább egy dolgozót.', 'warning'); return; }
    if (!this.kimenet.osszefuzott && !this.kimenet.kulon && !this.kimenet.docx) {
      this.data.sendToast('Válassz legalább egy kimenetet.', 'warning');
      return;
    }
    this.mentesUtan(() => this.generalasFut(kivalasztva));
  }

  private generalasFut(kivalasztva: any[]) {
    // A kimenet már a törzsben van (az előnézet-PDF is használja).
    const body = {
      ...this.torzsBody(),
      adidk: kivalasztva.map((d) => d.adid),
    };

    // Ha van letölthető fájl (összefűzött PDF vagy DOCX), blobként kérjük; különben JSON.
    const fajlKimenet = this.kimenet.osszefuzott || this.kimenet.docx;
    this.folyamatban = true;
    this.data.showLoading(`${kivalasztva.length} dokumentum generálása…`);

    if (fajlKimenet) {
      this.http.post(`/api/doksigenerator/generalas/${this.intezmenyId}`, body,
        { responseType: 'blob' }).subscribe({
        next: (blob: Blob) => {
          this.folyamatban = false;
          this.data.hideLoading();
          const zip = this.kimenet.docx;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const nev = (this.sablonok.find((s) => s.id === this.sablon)?.name || 'dokumentum')
            .replace(/[^\wÀ-ſ-]+/g, '_');
          a.download = `${nev}-${this.ev}.${zip ? 'zip' : 'pdf'}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.data.sendToast(`${kivalasztva.length} dokumentum elkészült.`, 'success');
        },
        error: async (e) => {
          this.folyamatban = false;
          this.data.hideLoading();
          let uzenet = 'A generálás nem sikerült.';
          if (e?.error instanceof Blob) {
            try { uzenet = JSON.parse(await e.error.text())?.error || uzenet; } catch {}
          }
          this.data.sendToast(uzenet, 'error');
        },
      });
    } else {
      this.http.post<any>(`/api/doksigenerator/generalas/${this.intezmenyId}`, body).subscribe({
        next: (r) => {
          this.folyamatban = false;
          this.data.hideLoading();
          const hiba = (r?.eredmenyek || []).filter((x: any) => x.hiba);
          if (hiba.length) {
            this.data.sendToast(
              `${(r.eredmenyek.length - hiba.length)} kész, ${hiba.length} hibás: `
              + hiba.map((x: any) => `${x.nev} (${x.hiba})`).join('; '), 'warning');
          } else {
            this.data.sendToast(`${r?.eredmenyek?.length || 0} dokumentum a dokumentumtárba került.`, 'success');
          }
        },
        error: (e) => {
          this.folyamatban = false;
          this.data.hideLoading();
          this.data.sendToast(e?.error?.error || 'A generálás nem sikerült.', 'error');
        },
      });
    }
  }
}
